import React, { useEffect, useRef } from 'react';
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  opacityDir: number;
  color: string;
}
interface ParticlesCanvasProps {
  className?: string;
  count?: number;
  colors?: string[];
  /** Draw faint lines between nearby particles */
  connected?: boolean;
  connectionDistance?: number;
  speed?: number;
}
export function ParticlesCanvas({
  className = '',
  count = 55,
  colors = ['#74c0fc', '#a5d8ff', '#4dabf7', '#228be6', '#e7f5ff'],
  connected = true,
  connectionDistance = 110,
  speed = 0.35
}: ParticlesCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let particles: Particle[] = [];
    let width = 0;
    let height = 0;
    const resize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    };
    const spawn = (): Particle => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * speed * 2,
      vy: (Math.random() - 0.5) * speed * 2,
      radius: 1.2 + Math.random() * 2.8,
      opacity: 0.15 + Math.random() * 0.55,
      opacityDir: Math.random() > 0.5 ? 1 : -1,
      color: colors[Math.floor(Math.random() * colors.length)]
    });
    const init = () => {
      resize();
      particles = Array.from({
        length: count
      }, spawn);
    };
    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      // Draw connection lines
      if (connected) {
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < connectionDistance) {
              const alpha = (1 - dist / connectionDistance) * 0.18;
              ctx.beginPath();
              ctx.strokeStyle = `rgba(116,192,252,${alpha})`;
              ctx.lineWidth = 0.8;
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.stroke();
            }
          }
        }
      }
      // Draw particles
      for (const p of particles) {
        // Gentle opacity breathing
        p.opacity += p.opacityDir * 0.003;
        if (p.opacity > 0.7) {
          p.opacity = 0.7;
          p.opacityDir = -1;
        }
        if (p.opacity < 0.1) {
          p.opacity = 0.1;
          p.opacityDir = 1;
        }
        // Move
        p.x += p.vx;
        p.y += p.vy;
        // Wrap edges
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    const ro = new ResizeObserver(() => {
      resize();
    });
    ro.observe(canvas);
    init();
    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [count, connected, connectionDistance, speed]);
  return <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} data-id="element-964" />;
}